import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { isCardShellEnabled, useIsCardShell } from './cardShell/shellVariant';

const rabbit_icon = '/assets/home/skating-rabbit.svg';

export const LoadingRabbit: React.FC<{
  title?: string;
  message?: string;
  className?: string;
}> = ({
  title = 'Loading Dashboard',
  message = 'Please wait while we load your business accounts...',
  className,
}) => (
  <div className={cn('text-center', className)}>
    <motion.div
      animate={{ x: [-15, 15, -15], rotate: [-5, 5, -5] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      className="mb-6"
    >
      <img src={rabbit_icon} alt="Loading..." className="h-28 w-28 mx-auto" />
    </motion.div>
    <h3 className="text-xl font-bold mb-2 tracking-tight text-[#08090A]">{title}</h3>
    <p className="text-muted-foreground animate-pulse">{message}</p>
  </div>
);

export const PageLoadingRabbit: React.FC<{
  title?: string;
  message?: string;
}> = (props) => {
  const isCardShell = useIsCardShell();

  if (isCardShell) {
    return (
      <div className="flex w-full flex-1 items-center justify-center px-6 py-16">
        <LoadingRabbit {...props} />
      </div>
    );
  }

  return (
    <div className="bg-[#F5F7F9] min-h-screen px-[30px] py-[32px] pt-[100px] flex items-center justify-center">
      <LoadingRabbit {...props} />
    </div>
  );
};

const NavRowSkeleton: React.FC<{ index: number }> = ({ index }) => (
  <div className="flex items-center gap-2.5 py-1.5 px-[10px]">
    <div className="wenext-shimmer w-5 h-5 rounded-md flex-shrink-0" />
    <div
      className="wenext-shimmer h-4 rounded-md"
      style={{ width: `${55 + ((index * 13) % 35)}%` }}
    />
  </div>
);

const NAV_ROWS = Array.from({ length: 12 }, (_, i) => i);

export const ShellLoadingScreen: React.FC<{
  search?: string;
  showWorkspaceChrome?: boolean;
}> = ({ search, showWorkspaceChrome = true }) => {
  const isCardShell = isCardShellEnabled(search);

  if (!isCardShell) {
    return (
      <div className="min-h-screen flex bg-[#F5F7F9]" aria-busy="true" aria-label="Loading">
        <aside className="hidden md:flex flex-col w-[240px] border-r border-[#E2E8F0] bg-white px-3 py-2 gap-2">
          <div className="wenext-shimmer h-8 w-32 rounded-md mt-2 mb-4" />
          <div className="flex flex-col gap-2 pt-[35px]">
            {NAV_ROWS.map((i) => (
              <NavRowSkeleton key={i} index={i} />
            ))}
          </div>
        </aside>
        <main className="flex-1 flex flex-col min-w-0">
          <div className="h-14 border-b border-[#E2E8F0] bg-white px-4 flex items-center gap-3">
            <div className="wenext-shimmer h-9 flex-1 max-w-[420px] rounded-md" />
            <div className="ml-auto flex items-center gap-2">
              <div className="wenext-shimmer h-8 w-28 rounded-md" />
              <div className="wenext-shimmer h-8 w-24 rounded-md" />
              <div className="wenext-shimmer h-8 w-8 rounded-full" />
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <LoadingRabbit />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div
      className="flex h-svh w-full overflow-hidden bg-app-ground"
      aria-busy="true"
      aria-label="Loading"
    >
      <aside className="hidden w-[244px] shrink-0 flex-col sm:flex">
        <div className="flex h-14 shrink-0 items-center px-3 sm:h-16 md:h-[62px]">
          <div className="wenext-shimmer h-7 w-[110px] rounded-md" />
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-3 py-3">
          {showWorkspaceChrome && (
            <div className="wenext-shimmer h-[38px] w-full shrink-0 rounded-lg" />
          )}
          <div className="flex flex-col gap-2 pt-[10px]">
            {NAV_ROWS.map((i) => (
              <NavRowSkeleton key={i} index={i} />
            ))}
          </div>
        </div>

        {showWorkspaceChrome && (
          <div className="shrink-0 px-3 pb-3 pt-2">
            <div className="wenext-shimmer h-[148px] w-full rounded-xl" />
          </div>
        )}
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden sm:my-3 sm:mr-3">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-0 bg-card shadow-[0_1px_2px_rgba(16,24,40,0.04)] sm:rounded-2xl sm:border sm:border-[#E4E7EC]">
          <div className="flex shrink-0 items-center gap-2 border-b border-[#E4E7EC] px-3 py-3 lg:gap-3 lg:px-[25px] lg:py-4">
            <div className="wenext-shimmer h-7 w-7 shrink-0 rounded-md sm:hidden" />
            <div className="wenext-shimmer h-9 min-w-0 flex-1 rounded-md sm:hidden" />

            <div className="hidden min-w-[200px] flex-1 sm:block">
              <div className="wenext-shimmer h-7 w-[180px] rounded-md" />
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-1.5 lg:gap-2">
              <div className="wenext-shimmer hidden h-9 w-[260px] rounded-md sm:block lg:w-[300px]" />
              <div className="wenext-shimmer hidden h-9 w-[104px] rounded-[6px] sm:block" />
              <div className="wenext-shimmer h-9 w-9 rounded-lg" />
              <div className="wenext-shimmer h-9 w-9 rounded-full" />
            </div>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden px-4">
            <LoadingRabbit />
          </div>
        </div>
      </div>
    </div>
  );
};
